/* eslint-disable @typescript-eslint/no-floating-promises */
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Pressable, StyleSheet, View } from 'react-native'

import { Key } from '@ambire-common/interfaces/keystore'
import { isSmartAccount } from '@ambire-common/libs/account/account'
import { humanizeMessage } from '@ambire-common/libs/humanizer'
import {
  EIP_1271_NOT_SUPPORTED_BY,
  toPersonalSignHex
} from '@ambire-common/libs/signMessage/signMessage'
import NoKeysToSignAlert from '@common/components/NoKeysToSignAlert'
import Spinner from '@common/components/Spinner'
import Text from '@common/components/Text'
import useTheme from '@common/hooks/useTheme'
import useToast from '@common/hooks/useToast'
import { SPACING_MD } from '@common/styles/spacings'
import flexbox from '@common/styles/utils/flexbox'
import SmallNotificationWindowWrapper from '@web/components/SmallNotificationWindowWrapper'
import { FEATURES } from '@web/constants/features'
import { TabLayoutContainer } from '@web/components/TabLayoutWrapper/TabLayoutWrapper'
import useBackgroundService from '@web/hooks/useBackgroundService'
import useDappInfo from '@web/hooks/useDappInfo/useDappInfo'
import useKeystoreControllerState from '@web/hooks/useKeystoreControllerState'
import useNetworksControllerState from '@web/hooks/useNetworksControllerState'
import useRequestsControllerState from '@web/hooks/useRequestsControllerState'
import useSelectedAccountControllerState from '@web/hooks/useSelectedAccountControllerState'
import useSignMessageControllerState from '@web/hooks/useSignMessageControllerState'
import useLedger from '@web/modules/hardware-wallet/hooks/useLedger'
import useQrSigningFlow from '@web/modules/hardware-wallet/hooks/useQrSigningFlow'
import SigningKeySelect from '@web/modules/sign-message/components/SignKeySelect'

import Authorization7702 from './Contents/authorization7702'
import Main from './Contents/main'
import SignInWithEthereum from './Contents/signInWithEthereum'

const SignMessageScreen = () => {
  const { t } = useTranslation()
  const signMessageState = useSignMessageControllerState()
  const signStatus = signMessageState.statuses.sign
  const [hasReachedBottom, setHasReachedBottom] = useState<boolean | null>(null)
  const keystoreState = useKeystoreControllerState()
  const { account } = useSelectedAccountControllerState()
  const { networks } = useNetworksControllerState()
  const { dispatch } = useBackgroundService()
  const { isLedgerConnected } = useLedger()
  const {
    currentRequest,
    signingStep,
    moveToResponseScan,
    moveBack,
    submitSignatureResponse,
    signingCleanup
  } = useQrSigningFlow()
  const [isChooseSignerShown, setIsChooseSignerShown] = useState(false)
  const [shouldDisplayLedgerConnectModal, setShouldDisplayLedgerConnectModal] = useState(false)
  const [shouldDisplayQrSigningModal, setShouldDisplayQrSigningModal] = useState(false)
  const [makeItSmartConfirmed, setMakeItSmartConfirmed] = useState(false)
  const [doNotAskMeAgain, setDoNotAskMeAgain] = useState(false)
  const { currentUserRequest } = useRequestsControllerState()
  const { theme } = useTheme()
  const { addToast } = useToast()

  const userRequest = useMemo(() => {
    if (
      currentUserRequest?.kind === 'message' ||
      currentUserRequest?.kind === 'typedMessage' ||
      currentUserRequest?.kind === 'authorization-7702' ||
      currentUserRequest?.kind === 'siwe'
    )
      return currentUserRequest

    return undefined
  }, [currentUserRequest])

  const { name, icon } = useDappInfo(userRequest)

  const isAuthorization = useMemo(() => {
    if (!userRequest) return false
    if (userRequest.kind !== 'authorization-7702') return false
    if (!userRequest.meta.show7702Info) return false

    return true
  }, [userRequest])

  const isSiwe = useMemo(() => {
    if (!userRequest) return false

    return userRequest.kind === 'siwe'
  }, [userRequest])

  const selectedAccountKeyStoreKeys = useMemo(
    () => keystoreState.keys.filter((key) => account?.associatedKeys.includes(key.addr)),
    [keystoreState.keys, account?.associatedKeys]
  )

  const network = useMemo(
    () =>
      networks.find((n) => {
        return signMessageState.messageToSign?.content.kind === 'typedMessage' &&
          signMessageState.messageToSign?.content.domain.chainId
          ? n.chainId.toString() === signMessageState.messageToSign?.content.domain.chainId
          : n.chainId === signMessageState.messageToSign?.chainId
      }),
    [networks, signMessageState.messageToSign]
  )
  const isViewOnly = useMemo(
    () => selectedAccountKeyStoreKeys.length === 0,
    [selectedAccountKeyStoreKeys.length]
  )
  const humanizedMessage = useMemo(() => {
    if (!signMessageState?.messageToSign) return
    return humanizeMessage(signMessageState.messageToSign)
  }, [signMessageState])

  const humanizationHasBlockingWarnings = useMemo(
    () => !!humanizedMessage?.warnings?.some((w) => w.blocking),
    [humanizedMessage?.warnings]
  )

  const visualizeHumanized = useMemo(
    () =>
      humanizedMessage?.fullVisualization &&
      network &&
      signMessageState.messageToSign?.content.kind,
    [network, humanizedMessage, signMessageState.messageToSign?.content?.kind]
  )

  const isScrollToBottomForced = useMemo(
    () => typeof hasReachedBottom === 'boolean' && !hasReachedBottom && !visualizeHumanized,
    [hasReachedBottom, visualizeHumanized]
  )

  useEffect(() => {
    const isAlreadyInit = signMessageState.messageToSign?.fromRequestId === userRequest?.id

    if (!userRequest || !userRequest || isAlreadyInit) return

    // Similarly to other wallets, attempt to normalize the input to a hex string,
    // because some dapps not always pass hex strings, but plain text or Uint8Array.
    if (userRequest.kind === 'message' || userRequest.kind === 'siwe')
      userRequest.meta.params.message = toPersonalSignHex(userRequest.meta.params.message)

    dispatch({
      type: 'MAIN_CONTROLLER_SIGN_MESSAGE_INIT',
      params: {
        dapp: { name, icon },
        messageToSign: {
          fromRequestId: userRequest.id,
          content: {
            kind: userRequest.kind,
            ...(userRequest.meta.params as any)
          },
          accountAddr: userRequest.meta.accountAddr,
          chainId: userRequest.meta.chainId,
          signature: null
        }
      }
    })
  }, [dispatch, userRequest, signMessageState.messageToSign?.fromRequestId, name, icon])

  useEffect(() => {
    return () => {
      dispatch({ type: 'MAIN_CONTROLLER_SIGN_MESSAGE_RESET' })
    }
  }, [dispatch])

  const handleReject = () => {
    if (!userRequest) return

    dispatch({
      type: 'REQUESTS_CONTROLLER_REJECT_USER_REQUEST',
      params: {
        err: t('User rejected the request.'),
        id: userRequest.id
      }
    })
  }

  // SMART_ACCOUNT_ENABLED=false: the EIP-7702 "Add smart features" prompt is a
  // Smart Account surface — auto-skip it (same as the user's "Skip for now").
  useEffect(() => {
    if (FEATURES.smartAccountEnabled || !isAuthorization || !userRequest) return
    dispatch({
      type: 'REQUESTS_CONTROLLER_REJECT_USER_REQUEST',
      params: { err: 'Smart Accounts temporarily disabled', id: userRequest.id }
    })
  }, [dispatch, isAuthorization, userRequest])

  const handleSign = useCallback(
    (chosenSigningKeyAddr?: Key['addr'], chosenSigningKeyType?: Key['type']) => {
      if (isAuthorization && !makeItSmartConfirmed) {
        setMakeItSmartConfirmed(true)
        setDoNotAskMeAgain(false)
        return
      }

      // Has more than one key, should first choose the key to sign with
      const hasChosenSigningKey = chosenSigningKeyAddr && chosenSigningKeyType
      const hasMultipleKeys = selectedAccountKeyStoreKeys.length > 1
      if (hasMultipleKeys && !hasChosenSigningKey) {
        return setIsChooseSignerShown(true)
      }

      const isLedgerKeyChosen = hasMultipleKeys
        ? // Accounts with multiple keys have an additional step to choose the key first
          chosenSigningKeyType === 'ledger'
        : // Take the key type from the account key itself, no additional step to choose key
          selectedAccountKeyStoreKeys[0]?.type === 'ledger'
      if (isLedgerKeyChosen && !isLedgerConnected) {
        setShouldDisplayLedgerConnectModal(true)
        return
      }

      const keyAddr = chosenSigningKeyAddr || selectedAccountKeyStoreKeys[0]?.addr
      const keyType = chosenSigningKeyType || selectedAccountKeyStoreKeys[0]?.type

      if (!keyAddr || !keyType) {
        addToast(
          t(
            'No signing key available to sign the message. Please reject the request and try again.'
          ),
          { type: 'error' }
        )
        return
      }

      if (keyType === 'qr') {
        // The QR flow is part of the signing itself (scan request -> scan response),
        // so we surface its modal and let the dispatch drive the QR controller.
        setShouldDisplayQrSigningModal(true)
      }

      dispatch({
        type: 'MAIN_CONTROLLER_HANDLE_SIGN_MESSAGE',
        params: { keyAddr, keyType }
      })
    },
    [
      isAuthorization,
      makeItSmartConfirmed,
      selectedAccountKeyStoreKeys,
      isLedgerConnected,
      dispatch,
      addToast,
      t
    ]
  )

  const resolveButtonText = useMemo(() => {
    if (isSiwe) return t('Sign in')
    if (isScrollToBottomForced) return t('Read the message')

    if (signStatus === 'LOADING') return t('Signing...')

    if (isAuthorization && !makeItSmartConfirmed) return 'Add smart features'

    return t('Sign')
  }, [isSiwe, t, isScrollToBottomForced, signStatus, isAuthorization, makeItSmartConfirmed])

  const rejectButtonText = useMemo(() => {
    if (isAuthorization && doNotAskMeAgain) return 'Skip'
    if (isAuthorization) return 'Skip for now'
    return 'Reject'
  }, [isAuthorization, doNotAskMeAgain])

  const handleDismissLedgerConnectModal = useCallback(() => {
    setShouldDisplayLedgerConnectModal(false)
  }, [])

  const handleQrSigningFlowOnClosePressed = useCallback(
    () => setShouldDisplayQrSigningModal(false),
    []
  )
  const handleQrSingingFlowOnContinuePressed = moveToResponseScan
  const handleQrSigningFlowOnBackPressed = moveBack
  const handleQrSigningFlowSubmitSignatureResponse = submitSignatureResponse
  const handleQrSigningFlowOnRejectPressed = useCallback(() => {
    signingCleanup()
    setShouldDisplayQrSigningModal(false)
  }, [signingCleanup])

  useEffect(() => {
    const hasActiveQrFlow = !!currentRequest || (!!signingStep && signingStep !== 'idle')
    if ((signMessageState as any).signingKeyType === 'qr' && hasActiveQrFlow) {
      setShouldDisplayQrSigningModal(true)
    }
  }, [currentRequest, signingStep, (signMessageState as any).signingKeyType])

  const shouldDisplayEIP1271Warning = useMemo(() => {
    const dappOrigin = userRequest?.dappPromises[0]?.session?.origin

    if (!dappOrigin || !isSmartAccount(account)) return false

    return EIP_1271_NOT_SUPPORTED_BY.some((origin) => dappOrigin.includes(origin))
  }, [account, userRequest?.dappPromises])

  const onDoNotAskMeAgainChange = useCallback(() => {
    setDoNotAskMeAgain(!doNotAskMeAgain)
  }, [doNotAskMeAgain])

  const view = useMemo(() => {
    // Happens when switching between requests
    const isReinitializingAfterSwitch =
      userRequest?.kind &&
      signMessageState.messageToSign &&
      userRequest.kind !== signMessageState.messageToSign.content.kind

    if (isReinitializingAfterSwitch) return 'reinitializing'

    if (isAuthorization && !makeItSmartConfirmed) return 'authorization-7702'

    if (isSiwe) return 'siwe'

    return 'sign-message'
  }, [
    isAuthorization,
    isSiwe,
    makeItSmartConfirmed,
    signMessageState.messageToSign,
    userRequest?.kind
  ])

  // In the split second when the request window opens, but the state is not yet
  // initialized, to prevent a flash of the fallback visualization, show a
  // loading spinner instead (would better be a skeleton, but whatever).
  if (!signMessageState.isInitialized || !account || !userRequest) {
    return (
      <View style={[StyleSheet.absoluteFill, flexbox.center]}>
        <Spinner />
      </View>
    )
  }

  return (
    <SmallNotificationWindowWrapper>
      <TabLayoutContainer
        width="full"
        // Default request-window horizontal padding is SPACING_3XL (64px) — too
        // wide for this modal on desktop. Drop the built-in padding and match it
        // to the vertical rhythm (SPACING_MD) so the popup reads evenly boxed.
        withHorizontalPadding={false}
        wrapperStyle={{ paddingHorizontal: SPACING_MD }}
        footerContainerStyle={{
          backgroundColor: 'transparent',
          maxHeight: undefined,
          shadowOpacity: 0,
          shadowColor: 'transparent',
          paddingTop: 8,
          paddingBottom: 28,
          paddingHorizontal: SPACING_MD
        }}
        footer={
          isViewOnly ? (
            <View style={[flexbox.flex1, flexbox.directionRow, flexbox.justifyEnd]}>
              <NoKeysToSignAlert type="short" isTransaction={false} />
            </View>
          ) : (
            <View style={[flexbox.flex1, flexbox.directionRow, flexbox.alignCenter, { gap: 12 }]}>
              <Pressable
                onPress={handleReject}
                style={({ hovered }: any) => [
                  flexbox.flex1,
                  flexbox.center,
                  {
                    height: 50,
                    borderRadius: 8,
                    borderWidth: 1,
                    borderColor: theme.errorDecorative as string,
                    backgroundColor: hovered
                      ? (theme.dangerBackground as string)
                      : 'transparent'
                  }
                ]}
                testID="button-reject"
              >
                <Text fontSize={16} weight="medium" color={theme.errorText as string}>
                  {rejectButtonText}
                </Text>
              </Pressable>
              <Pressable
                onPress={() => handleSign()}
                disabled={
                  signStatus === 'LOADING' ||
                  isScrollToBottomForced ||
                  humanizationHasBlockingWarnings
                }
                style={({ hovered }: any) => [
                  flexbox.flex1,
                  flexbox.directionRow,
                  flexbox.center,
                  {
                    height: 50,
                    borderRadius: 8,
                    gap: 8,
                    backgroundColor: theme.accentChipDecorative as string,
                    opacity:
                      signStatus === 'LOADING' ||
                      isScrollToBottomForced ||
                      humanizationHasBlockingWarnings
                        ? 0.5
                        : hovered
                          ? 0.9
                          : 1
                  }
                ]}
                testID="button-sign"
              >
                {signStatus === 'LOADING' && <Spinner style={{ width: 18, height: 18 }} />}
                <Text fontSize={16} weight="medium" color={theme.white as string}>
                  {resolveButtonText}
                </Text>
              </Pressable>
            </View>
          )
        }
        backgroundColor={
          isAuthorization && !makeItSmartConfirmed
            ? theme.primaryBackground
            : theme.quinaryBackground
        }
      >
        <SigningKeySelect
          isVisible={isChooseSignerShown}
          isSigning={signStatus === 'LOADING'}
          selectedAccountKeyStoreKeys={selectedAccountKeyStoreKeys}
          handleChooseKey={handleSign}
          type="signing"
          handleClose={() => setIsChooseSignerShown(false)}
          account={account}
        />
        {view === 'reinitializing' && (
          <View style={[StyleSheet.absoluteFill, flexbox.center]}>
            <Spinner />
          </View>
        )}
        {view === 'authorization-7702' && (
          <Authorization7702
            onDoNotAskMeAgainChange={onDoNotAskMeAgainChange}
            doNotAskMeAgain={doNotAskMeAgain}
            displayFullInformation
          />
        )}
        {view === 'sign-message' && (
          <Main
            shouldDisplayLedgerConnectModal={shouldDisplayLedgerConnectModal}
            isLedgerConnected={isLedgerConnected}
            handleDismissLedgerConnectModal={handleDismissLedgerConnectModal}
            hasReachedBottom={hasReachedBottom}
            setHasReachedBottom={setHasReachedBottom}
            shouldDisplayEIP1271Warning={shouldDisplayEIP1271Warning}
            shouldDisplayQrSigningModal={shouldDisplayQrSigningModal}
            currentRequest={currentRequest}
            signingStep={signingStep}
            handleQrSigningFlowOnClosePressed={handleQrSigningFlowOnClosePressed}
            handleQrSingingFlowOnContinuePressed={handleQrSingingFlowOnContinuePressed}
            handleQrSigningFlowSubmitSignatureResponse={handleQrSigningFlowSubmitSignatureResponse}
            handleQrSigningFlowOnRejectPressed={handleQrSigningFlowOnRejectPressed}
            handleQrSigningFlowOnBackPressed={handleQrSigningFlowOnBackPressed}
          />
        )}
        {view === 'siwe' && (
          <SignInWithEthereum
            shouldDisplayLedgerConnectModal={shouldDisplayLedgerConnectModal}
            isLedgerConnected={isLedgerConnected}
            handleDismissLedgerConnectModal={handleDismissLedgerConnectModal}
            shouldDisplayQrSigningModal={shouldDisplayQrSigningModal}
            currentRequest={currentRequest}
            signingStep={signingStep}
            handleQrSigningFlowOnClosePressed={handleQrSigningFlowOnClosePressed}
            handleQrSingingFlowOnContinuePressed={handleQrSingingFlowOnContinuePressed}
            handleQrSigningFlowSubmitSignatureResponse={handleQrSigningFlowSubmitSignatureResponse}
            handleQrSigningFlowOnRejectPressed={handleQrSigningFlowOnRejectPressed}
            handleQrSigningFlowOnBackPressed={handleQrSigningFlowOnBackPressed}
          />
        )}
      </TabLayoutContainer>
    </SmallNotificationWindowWrapper>
  )
}

export default React.memo(SignMessageScreen)
